import {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {ORG_MESSAGES as M} from '../organizationMessages'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {
  CREATE_UPDATE_ORGANIZATION,
  CREATE_UPDATE_ORGANIZATION_STR,
} from '../../../gql/mutations/organizationMutations'
import {useUploadFile} from '../../../hooks/useUploadFile'

const EMPTY_ORG = {
  name: '',
  owner: '',
  email: '',
  phone: '',
  website: '',
  streetAddress: '',
  city: '',
  country: 'Estados Unidos',
  state: '',
  postalCode: '',
}

/**
 * Owns the General-tab form state (create or update, keyed off whether `org` is
 * passed) plus the logo / template-logo upload flows. Shared by OneOrganizationPage
 * (editing an existing org) and OrganizationsPage's "New Organization" modal
 * (org is undefined — mutation runs without an id, i.e. creates).
 */
export const useOrganizationForm = (org?: any, onSaved?: (org: any) => void) => {
  const [createUpdateOrg] = useMutation(CREATE_UPDATE_ORGANIZATION)
  const [form, setForm] = useState<any>({...EMPTY_ORG})
  const [saving, setSaving] = useState(false)

  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null)
  const [logoLoadError, setLogoLoadError] = useState(false)
  const [uploadFile, {data: uploadData, loading: uploadingLogo, error: uploadError}] =
    useUploadFile<any>()
  const logoInputRef = useRef<HTMLInputElement>(null)

  const [tplLogoPreview, setTplLogoPreview] = useState<string | null>(null)
  const [selectedTplLogo, setSelectedTplLogo] = useState<File | null>(null)
  const [tplLogoLoadError, setTplLogoLoadError] = useState(false)
  const [uploadTplFile, {data: uploadTplData, loading: uploadingTplLogo, error: uploadTplError}] =
    useUploadFile<any>()
  const tplLogoInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!org) {
      setForm({...EMPTY_ORG})
      return
    }
    setForm({
      name: org.name || '',
      owner: org.owner || '',
      email: org.email || '',
      phone: org.phone || '',
      website: org.website || '',
      streetAddress: org.streetAddress || '',
      city: org.city || '',
      country: org.country || '',
      state: org.state || '',
      postalCode: org.postalCode || '',
    })
    setLogoPreview(null)
    setSelectedLogo(null)
    setLogoLoadError(false)
    setTplLogoPreview(null)
    setSelectedTplLogo(null)
    setTplLogoLoadError(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.id])

  const intl = useIntl()

  const handleChange = (field: string, value: string) => {
    setForm((prev: any) => ({...prev, [field]: value}))
  }

  const handleSave = async () => {
    if (!form.name || !form.owner || !form.email || !form.phone) {
      toast.warning(intl.formatMessage(M.errRequiredFields))
      return
    }
    setSaving(true)
    try {
      const variables: any = {
        name: form.name,
        owner: form.owner,
        email: form.email,
        phone: form.phone,
        website: form.website || '',
        streetAddress: form.streetAddress,
        city: form.city,
        country: form.country,
        state: form.state || '',
        postalCode: form.postalCode,
        isActive: true,
      }
      if (org?.id) variables.id = org.id

      const res = await createUpdateOrg({variables})
      const errors = res.data?.organizationCreateupdateMutation?.errors
      if (errors && errors.length > 0) {
        const msgs = errors.map((e: any) => `${e.field}: ${e.messages.join(', ')}`).join('\n')
        toast.error(intl.formatMessage(M.errValidation, {messages: msgs}))
      } else {
        toast.success(intl.formatMessage(org ? M.toastSaved : M.toastCreated))
        onSaved?.(res.data?.organizationCreateupdateMutation?.organization)
      }
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errSaving))
    } finally {
      setSaving(false)
    }
  }

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedLogo(file)
      setLogoLoadError(false)
      const reader = new FileReader()
      reader.onloadend = () => setLogoPreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleLogoUpload = (orgId: string) => {
    if (!selectedLogo || !orgId) return
    const formdata = new FormData()
    formdata.append('0', selectedLogo, selectedLogo.name)
    const map = {'0': ['variables.logo']}
    const variables: any = {
      id: orgId,
      name: form.name,
      owner: form.owner,
      email: form.email,
      phone: form.phone,
      website: form.website || '',
      streetAddress: form.streetAddress,
      city: form.city,
      country: form.country,
      state: form.state || '',
      postalCode: form.postalCode,
      isActive: true,
      logo: null,
    }
    formdata.append('map', JSON.stringify(map))
    formdata.append(
      'operations',
      JSON.stringify({query: CREATE_UPDATE_ORGANIZATION_STR, variables})
    )
    uploadFile(formdata)
  }

  useEffect(() => {
    if (!uploadData) return
    const uploadedOrg = uploadData?.organizationCreateupdateMutation?.organization
    if (uploadedOrg?.id) {
      toast.success(intl.formatMessage(M.toastLogoUpdated))
      setSelectedLogo(null)
      setLogoPreview(null)
      setLogoLoadError(false)
      onSaved?.(uploadedOrg)
    }
    const errors = uploadData?.organizationCreateupdateMutation?.errors
    if (errors?.length > 0) {
      toast.error(
        intl.formatMessage(M.errLogo, {
          message: errors[0]?.messages?.join(', ') || intl.formatMessage(M.unknownError),
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadData])

  useEffect(() => {
    if (uploadError) toast.error(intl.formatMessage(M.errUploadingLogo))
  }, [uploadError])

  const handleTplLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedTplLogo(file)
      setTplLogoLoadError(false)
      const reader = new FileReader()
      reader.onloadend = () => setTplLogoPreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleTplLogoUpload = (orgId: string) => {
    if (!selectedTplLogo || !orgId) return
    const formdata = new FormData()
    formdata.append('0', selectedTplLogo, selectedTplLogo.name)
    const map = {'0': ['variables.templateLogo']}
    const variables: any = {
      id: orgId,
      name: form.name,
      owner: form.owner,
      email: form.email,
      phone: form.phone,
      website: form.website || '',
      streetAddress: form.streetAddress,
      city: form.city,
      country: form.country,
      state: form.state || '',
      postalCode: form.postalCode,
      isActive: true,
      templateLogo: null,
    }
    formdata.append('map', JSON.stringify(map))
    formdata.append(
      'operations',
      JSON.stringify({query: CREATE_UPDATE_ORGANIZATION_STR, variables})
    )
    uploadTplFile(formdata)
  }

  useEffect(() => {
    if (!uploadTplData) return
    const uploadedOrg = uploadTplData?.organizationCreateupdateMutation?.organization
    if (uploadedOrg?.id) {
      toast.success(intl.formatMessage(M.toastTplLogoUpdated))
      setSelectedTplLogo(null)
      setTplLogoPreview(null)
      setTplLogoLoadError(false)
      onSaved?.(uploadedOrg)
    }
    const errors = uploadTplData?.organizationCreateupdateMutation?.errors
    if (errors?.length > 0) {
      toast.error(
        intl.formatMessage(M.errTplLogo, {
          message: errors[0]?.messages?.join(', ') || intl.formatMessage(M.unknownError),
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadTplData])

  useEffect(() => {
    if (uploadTplError) toast.error(intl.formatMessage(M.errUploadingTplLogo))
  }, [uploadTplError])

  return {
    form,
    handleChange,
    handleSave,
    saving,
    logoPreview,
    setLogoPreview,
    selectedLogo,
    setSelectedLogo,
    logoLoadError,
    setLogoLoadError,
    logoInputRef,
    handleLogoSelect,
    handleLogoUpload,
    uploadingLogo,
    tplLogoPreview,
    setTplLogoPreview,
    selectedTplLogo,
    setSelectedTplLogo,
    tplLogoLoadError,
    setTplLogoLoadError,
    tplLogoInputRef,
    handleTplLogoSelect,
    handleTplLogoUpload,
    uploadingTplLogo,
  }
}
